import React, { useState, useEffect } from 'react';
import { Table, Typography, Tag, Space, Input, Select, Card, Row, Col, Statistic, DatePicker, Button, message, Popconfirm } from 'antd';
import { SearchOutlined, ClearOutlined, CheckCircleOutlined, CloseCircleOutlined, AuditOutlined, ThunderboltOutlined } from '@ant-design/icons';
import api from '../api';
import dayjs from 'dayjs';

const { Title, Text } = Typography;
const { RangePicker } = DatePicker;

const actionLabels = {
  'admin.create': { label: '创建管理员', color: 'red' },
  'admin.update': { label: '更新管理员', color: 'orange' },
  'admin.delete': { label: '删除管理员', color: 'red' },
  'user.disable': { label: '禁用用户', color: 'orange' },
  'user.enable': { label: '启用用户', color: 'green' },
  'user.mute': { label: '禁言用户', color: 'orange' },
  'user.unmute': { label: '解除禁言', color: 'green' },
  'user.blacklist': { label: '拉黑用户', color: 'red' },
  'plugin.toggle': { label: '插件开关', color: 'blue' },
  'plugin.create': { label: '创建插件', color: 'blue' },
  'plugin.update': { label: '更新插件', color: 'cyan' },
  'plugin.delete': { label: '删除插件', color: 'red' },
  'group.delete': { label: '解散群组', color: 'red' },
  'message.delete': { label: '删除消息', color: 'red' },
  'moment.delete': { label: '删除动态', color: 'red' },
  'tab.config': { label: 'Tab配置', color: 'purple' },
  'operation.config': { label: '运营配置', color: 'purple' },
  'app.config': { label: '应用配置', color: 'purple' },
  'sensitive_word.create': { label: '添加敏感词', color: 'volcano' },
  'sensitive_word.bulk_create': { label: '批量添加敏感词', color: 'volcano' },
  'sensitive_word.delete': { label: '删除敏感词', color: 'red' },
  'miniprogram.publish': { label: '小程序上架', color: 'green' },
  'miniprogram.unpublish': { label: '小程序下架', color: 'orange' },
  'banner.create': { label: '创建Banner', color: 'blue' },
  'banner.update': { label: '更新Banner', color: 'cyan' },
  'banner.delete': { label: '删除Banner', color: 'red' },
  'announcement.create': { label: '发布公告', color: 'blue' },
  'announcement.update': { label: '更新公告', color: 'cyan' },
  'announcement.delete': { label: '删除公告', color: 'red' },
  'material.create': { label: '上传素材', color: 'blue' },
  'material.delete': { label: '删除素材', color: 'red' },
};

const moduleOptions = [
  { value: 'admin', label: '管理员' },
  { value: 'user', label: '用户' },
  { value: 'plugin', label: '插件' },
  { value: 'group', label: '群组' },
  { value: 'message', label: '消息' },
  { value: 'moment', label: '动态' },
  { value: 'config', label: '配置' },
  { value: 'sensitive_word', label: '敏感词' },
  { value: 'miniprogram', label: '小程序' },
  { value: 'banner', label: 'Banner' },
  { value: 'announcement', label: '公告' },
  { value: 'material', label: '素材' },
];

const resultOptions = [
  { value: 'success', label: '成功' },
  { value: 'fail', label: '失败' },
];

function AuditLogPage() {
  const [logs, setLogs] = useState([]);
  const [total, setTotal] = useState(0);
  const [page, setPage] = useState(1);
  const [loading, setLoading] = useState(false);
  const [actionFilter, setActionFilter] = useState('');
  const [moduleFilter, setModuleFilter] = useState('');
  const [operatorFilter, setOperatorFilter] = useState('');
  const [resultFilter, setResultFilter] = useState('');
  const [dateRange, setDateRange] = useState(null);
  const [stats, setStats] = useState({ total_actions: 0, today_actions: 0, failed_actions: 0 });

  const loadLogs = async (p = 1) => {
    setLoading(true);
    try {
      const params = { page: p, limit: 20 };
      if (actionFilter) params.action = actionFilter;
      if (moduleFilter) params.module = moduleFilter;
      if (operatorFilter) params.operator = operatorFilter;
      if (resultFilter) params.result = resultFilter;
      if (dateRange && dateRange[0] && dateRange[1]) {
        params.start_date = dateRange[0].format('YYYY-MM-DD HH:mm:ss');
        params.end_date = dateRange[1].format('YYYY-MM-DD HH:mm:ss');
      }
      const { list, total: t } = await api.get('/admin/audit-logs', { params });
      setLogs(list);
      setTotal(t);
      setPage(p);
    } catch (e) {
      message.error('加载审计日志失败');
    } finally {
      setLoading(false);
    }
  };

  const loadStats = async () => {
    try {
      const data = await api.get('/admin/audit-logs/stats');
      setStats(data);
    } catch (e) {
      // ignore
    }
  };

  useEffect(() => {
    loadLogs();
    loadStats();
  }, []);

  const handleSearch = () => {
    loadLogs(1);
    loadStats();
  };

  const handleClearFilters = () => {
    setActionFilter('');
    setModuleFilter('');
    setOperatorFilter('');
    setResultFilter('');
    setDateRange(null);
    setTimeout(() => loadLogs(1), 0);
  };

  const handleCleanup = async () => {
    try {
      await api.delete('/admin/audit-logs/cleanup', { params: { days: 90 } });
      message.success('已清理90天前的日志');
      loadLogs(1);
      loadStats();
    } catch (e) {
      message.error('清理失败');
    }
  };

  const columns = [
    { title: 'ID', width: 80, dataIndex: 'id' },
    {
      title: '操作人', width: 140,
      render: (_, r) => (
        <Space>
          <span style={{ fontWeight: 'bold' }}>{r.admin_name || r.operator_name || '-'}</span>
          <Text type="secondary">#{r.admin_id || r.operator_id || '-'}</Text>
        </Space>
      ),
    },
    {
      title: '模块', width: 100,
      dataIndex: 'module',
      render: (v) => {
        const label = moduleOptions.find(m => m.value === v);
        return <Tag color="geekblue">{label?.label || v || '-'}</Tag>;
      },
    },
    {
      title: '操作类型', width: 160,
      render: (_, r) => {
        const label = actionLabels[r.action];
        return label ? <Tag color={label.color}>{label.label}</Tag> : <Tag>{r.action}</Tag>;
      },
    },
    {
      title: '目标', width: 140,
      render: (_, r) => (
        <span>
          {r.target_type ? <Tag>{r.target_type}</Tag> : null}
          {r.target_id ? <Text type="secondary">#{r.target_id}</Text> : null}
          {!r.target_type && !r.target_id && '-'}
        </span>
      ),
    },
    {
      title: '详情', dataIndex: 'detail', ellipsis: true, width: 200,
      render: (v) => v ? <Text type="secondary">{v}</Text> : '-',
    },
    { title: 'IP地址', dataIndex: 'ip_address', width: 130, render: (v) => v || '-' },
    {
      title: '结果', width: 100,
      dataIndex: 'result',
      render: (v) => v === 'success' ? (
        <Tag icon={<CheckCircleOutlined />} color="success">成功</Tag>
      ) : v === 'fail' ? (
        <Tag icon={<CloseCircleOutlined />} color="error">失败</Tag>
      ) : '-',
    },
    { title: '操作时间', dataIndex: 'created_at', width: 180, render: (v) => v ? new Date(v).toLocaleString() : '-' },
  ];

  return (
    <>
      <Title level={4}>操作审计日志</Title>

      {/* 统计卡片 */}
      <Row gutter={16} style={{ marginBottom: 16 }}>
        <Col span={8}>
          <Card>
            <Statistic
              title="总操作数"
              value={stats.total_actions || 0}
              prefix={<AuditOutlined style={{ color: '#1890ff' }} />}
            />
          </Card>
        </Col>
        <Col span={8}>
          <Card>
            <Statistic
              title="今日操作"
              value={stats.today_actions || 0}
              prefix={<ThunderboltOutlined style={{ color: '#52c41a' }} />}
            />
          </Card>
        </Col>
        <Col span={8}>
          <Card>
            <Statistic
              title="失败操作"
              value={stats.failed_actions || 0}
              prefix={<CloseCircleOutlined style={{ color: '#ff4d4f' }} />}
            />
          </Card>
        </Col>
      </Row>

      {/* 筛选区域 */}
      <Card style={{ marginBottom: 16 }}>
        <Space wrap>
          <Select
            placeholder="筛选操作类型"
            value={actionFilter}
            onChange={(v) => { setActionFilter(v); }}
            style={{ width: 180 }}
            allowClear
            options={Object.entries(actionLabels).map(([k, v]) => ({ value: k, label: v.label }))}
          />
          <Select
            placeholder="筛选模块"
            value={moduleFilter}
            onChange={setModuleFilter}
            style={{ width: 140 }}
            allowClear
            options={moduleOptions}
          />
          <Select
            placeholder="筛选结果"
            value={resultFilter}
            onChange={setResultFilter}
            style={{ width: 120 }}
            allowClear
            options={resultOptions}
          />
          <Input
            placeholder="操作人名称"
            value={operatorFilter}
            onChange={(e) => setOperatorFilter(e.target.value)}
            style={{ width: 150 }}
            allowClear
          />
          <RangePicker
            showTime
            format="YYYY-MM-DD HH:mm:ss"
            value={dateRange}
            onChange={setDateRange}
            placeholder={('开始时间', '结束时间')}
          />
          <Button type="primary" icon={<SearchOutlined />} onClick={handleSearch}>搜索</Button>
          <Button icon={<ClearOutlined />} onClick={handleClearFilters}>清除</Button>
          <Popconfirm title="确定清理90天前的日志?" onConfirm={handleCleanup}>
            <Button danger>清理旧日志</Button>
          </Popconfirm>
        </Space>
      </Card>

      <Card>
        <Table
          columns={columns}
          dataSource={logs}
          rowKey="id"
          loading={loading}
          pagination={{
            current: page,
            total,
            pageSize: 20,
            onChange: loadLogs,
            showSizeChanger: false,
          }}
          size="small"
          scroll={{ x: 1400 }}
        />
      </Card>
    </>
  );
}

export default AuditLogPage;
